import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { DateTime } from 'luxon'

import {
    fetchFundSummary,
    fetchInvoices,
} from '../../features/fundManagement/fundManagementSlice'

import './FundManagementDashboard.css'
import EmptyStateImage from '@/assets/images/user_dashboard_empty_state.webp'
import CButton from '../../components/UI/CButton'
import CDataGrid from '../../components/UI/CDataGrid'
import CDialog from '../../components/UI/CDialog'
import PurchaseFundsForm from './components/PurchaseFundsForm'
import Icon from '../../components/UI/Icon'

const FundManagementDashboard = () => {
    const dispatch = useDispatch()
    const location = useLocation()
    const {
        wallet,
        invoices,
        loading,
        error,
        totalInvoices
    } = useSelector((state) => state.fundManagement)

    const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false)
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 })

    useEffect(() => {
        dispatch(fetchFundSummary())
        dispatch(fetchInvoices({
            pageNumber: paginationModel.page + 1,
            pageSize: paginationModel.pageSize
        }))
    }, [dispatch, paginationModel])

    // Check for navigation state to open purchase dialog
    useEffect(() => {
        if (location.state?.openDialog === 'purchaseFunds') {
            setPurchaseDialogOpen(true)
            // Clear the state after opening dialog
            window.history.replaceState({}, document.title)
        }
    }, [location])

    const handlePurchaseClick = () => {
        setPurchaseDialogOpen(true)
    }

    const handlePurchaseClose = () => {
        setPurchaseDialogOpen(false)
    }

    const handlePurchaseSuccess = (amount) => {
        // Refresh invoices list after successful purchase with current pagination
        dispatch(fetchInvoices({
            pageNumber: paginationModel.page + 1,
            pageSize: paginationModel.pageSize
        }))
    }

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: wallet.currency,
        }).format(amount)
    }

    const columns = [
        {
            field: 'invoiceid',
            headerName: 'Invoice ID',
            flex: 1,
            minWidth: 140,
        },
        {
            field: 'creationdate',
            headerName: 'Date',
            flex: 1,
            minWidth: 140,
            valueFormatter: (params) => {
                return DateTime.fromISO(params).toLocaleString(DateTime.DATE_MED)
            },
        },
        {
            field: 'amount',
            headerName: 'Amount',
            flex: 1,
            minWidth: 130,
            valueFormatter: (params) => formatCurrency(params),
        },
        // {
        //     field: 'status',
        //     headerName: 'Status',
        //     flex: 0.8,
        //     minWidth: 110,
        //     renderCell: (params) => {
        //         const statusClass = `status-badge status-${params.value.toLowerCase()}`
        //         return (
        //             <span className={statusClass}>
        //                 {params.value}
        //             </span>
        //         )
        //     },
        // },
    ]

    return (
        <div className="fund-management-dashboard">
            {/* Header Section */}
            <div className="dashboard-header">
                <span className="dashboard-title header-2">Account</span>
                <CButton
                    label="Purchase Funds"
                    leftIcon={"add-circle"}
                    onClick={handlePurchaseClick}
                    variant="contained"
                    color="primary"
                    loading={loading.purchase}
                />
            </div>

            {/* Wallet Cards */}
            <div className="wallet-cards-grid">
                <div className="wallet-card total-purchased">
                    <div className="card-header">
                        <div className="card-header-inner">
                            <span className="card-title text-3">Total Purchased</span>
                            <div className="card-amount header-2">
                                {loading.wallet ? '...' : formatCurrency(wallet.purchasetotal)}
                            </div>
                        </div>

                        <div className="icon-wrapper">
                            <Icon name="analytics-bar-graph" />
                        </div>
                    </div>

                    <div className="divider"></div>

                    <p className="card-subtitle text-4">Total funds purchased to date</p>
                </div>

                <div className="wallet-card total-issued">
                    <div className="card-header">
                        <div className="card-header-inner">
                            <span className="card-title text-3">Total Issued</span>
                            <div className="card-amount header-2">
                                {loading.wallet ? '...' : formatCurrency(wallet.issuetotal)}
                            </div>
                        </div>

                        <div className="icon-wrapper">
                            <Icon name="analytics-bar-graph" />
                        </div>
                    </div>

                    <div className="divider"></div>

                    <p className="card-subtitle text-4">Total funds issued to date</p>
                </div>

                <div className="wallet-card current-balance">
                    <div className="card-header">
                        <div className="card-header-inner">
                            <span className="card-title text-3">Current Balance</span>
                            <div className="card-amount header-2">
                                {loading.wallet ? '...' : formatCurrency(wallet.balance)}
                            </div>
                        </div>

                        <div className="icon-wrapper">
                            <Icon name="analytics-bar-graph" />
                        </div>
                    </div>

                    <div className="divider"></div>

                    <p className="card-subtitle text-4">Available funds in your account</p>
                </div>
            </div>

            {/* Invoice History */}
            <div className="invoice-history">
                <span className="section-title header-3">Invoice History</span>
                <div className="datagrid-container">
                    <CDataGrid
                        rows={invoices}
                        columns={columns}
                        rowCount={totalInvoices}
                        loading={loading.invoices}
                        pageSizeOptions={[5, 10, 25, 50]}
                        initialPageSize={10}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        getRowId={(row) => row.invoiceid}
                        disableRowSelectionOnClick
                        emptyStateImage={EmptyStateImage}
                        emptyStateTitle="No invoices found"
                        emptyStateDescription="Your invoice history will appear here once you start purchasing funds."
                        emptyStateButtonLabel="Purchase funds"
                        emptyStateOnClick={handlePurchaseClick}
                    />
                </div>
            </div>

            {/* Purchase Dialog */}
            <CDialog
                open={purchaseDialogOpen}
                onClose={handlePurchaseClose}
                title="Purchase Funds"
                titleIcon={"credit-card-sync"}
                maxWidth="sm"
                fullWidth
            >
                <PurchaseFundsForm
                    onClose={handlePurchaseClose}
                    onSuccess={handlePurchaseSuccess}
                    currency={wallet.currency}
                />
            </CDialog>
        </div>
    )
}

export default FundManagementDashboard
